<template>
  <v-container>
    <v-row justify-center>
      <v-col>
        <v-divider />

        <!-- Titre personnalisé -->
        <v-row align="center" justify="center">
          <v-col cols="12" lg="3" xl="2">
            <h4 class="part-title">Titre personnalisé :</h4>
          </v-col>
          <v-col cols="12" lg="5" xl="3">
            <v-text-field
              v-model="currentDiscipline.ageGroupCustomTitle"
              @change="updateDisciplineCustomLabel(currentDiscipline.ageGroupCustomTitle)"
              placeholder="Catégorie d’âge"
              outlined
              dense
              class="mr-5"
              :rules="[rules.counter]"
              counter
              maxlength="30"
              clearable
              :disabled="!canManageCurrentDiscipline"
            />
          </v-col>
          <v-col cols="12" lg="4" xl="7">
            <span style="font-style: italic">Le titre personnalisé sera affiché à la place de "Catégorie d’âge".</span>
          </v-col>
        </v-row>

        <v-divider v-if="canManageCurrentDiscipline" />

        <!-- Add item -->
        <v-row v-if="canManageCurrentDiscipline">
          <v-col cols="12" lg="3" xl="2">
            <h4 class="part-title">Ajouter un élément :</h4>
          </v-col>
          <v-col cols="12" lg="5" xl="3">
            <v-text-field
              v-model="newAgeGroup.title"
              outlined
              dense
              class="mr-5"
              :rules="[rules.required, rules.counter]"
              counter
              maxlength="30"
              hide-details="auto"
              clearable
            />
          </v-col>
          <v-col cols="12" lg="4" xl="7">
            <v-btn v-on:click="addAgeGroup()" color="primary" dark class="mb-2" elevation="2" :disabled="!newAgeGroup.title"> Ajouter </v-btn>
          </v-col>
        </v-row>

        <v-divider />

        <!-- List of items -->
        <v-row v-if="ageGroups && ageGroups.length > 0">
          <v-col cols="12">
            <h4 class="part-title">{{ getCutomTitle() }}</h4>
          </v-col>
          <v-col>
            <v-row v-for="ageGroup in ageGroups" :key="ageGroup.id" class="ma-0 pa-1">
              <v-col cols="6" lg="6" xl="3" class="no-vert-padding">
                <v-text-field
                  @change="updateAgeGroup(ageGroup)"
                  v-model="ageGroup.title"
                  outlined
                  dense
                  class="mr-5"
                  :rules="[rules.required, rules.counter]"
                  counter
                  maxlength="30"
                  hide-details="auto"
                  :disabled="!canManageCurrentDiscipline"
                />
              </v-col>
              <v-col cols="6" lg="6" xl="9" class="no-vert-padding" v-if="canManageCurrentDiscipline">
                <v-btn v-on:click="deleteAgeGroup(ageGroup.id)" color="red" dark class="mb-2" elevation="2"> Supprimer </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import store from "@/store";
import PermissionsService from "@/services/PermissionsService";
import { getAgeGroupForDisciplineAsync, createAgeGroupForDisciplineAsync, updateAgeGroupAsync, deleteAgeGroupAsync } from "@/api/ageGroupApi";
import { getDisciplineAsync, updateDisciplineAsync, getIsManagerPartOfDisciplineAsync } from "@/api/disciplinesApi";
import { getIsUserPartOfFederationAsync } from "@/api/federationsApi";
import AuthService from "@/services/AuthService";

export default {
  props: {
    disciplineId_prop: [Number, String],
  },

  data: () => ({
    isCurrentUserPartOfFederation: false,
    isCurrentUserPartOfDiscipline: false,
    authService: AuthService,
    federationId: null,
    disciplineId: null,
    ageGroups: [],
    currentDiscipline: {},
    customTitle: "",
    newAgeGroup: { title: "Nouvel élément" },
    // Validation rules on cycle name (title)
    rules: {
      required: (value) => !!value || "Requis",
      counter: (value) => (value ? value.length <= 30 : true),
    },
  }),

  watch: {
    $route() {
      this.initPage();
    },
  },

  computed: {
    canViewCurrentDiscipline() {
      // Can view own discipline and is part of current discipline
      let canViewOwnDisciplineAndIsPartOfCurrentDiscipline = PermissionsService.DisciplinesPermissionsService.canViewOwnDiscipline && this.isCurrentUserPartOfDiscipline;
      // Can view all Disciplines
      let canViewAllDisciplines = PermissionsService.DisciplinesPermissionsService.canViewAllDisciplinesOwnFederation;
      return canViewOwnDisciplineAndIsPartOfCurrentDiscipline || canViewAllDisciplines;
    },
    canManageCurrentDiscipline() {
      // Can manage own discipline and is part of current discipline
      let canManageCurrentDisciplineAndIsPartOfCurrentDiscipline =
        PermissionsService.DisciplinesPermissionsService.canManageOwnDiscipline && this.isCurrentUserPartOfDiscipline;
      // Can manage all disciplines of the current federation
      let canManageAllDisciplines = PermissionsService.DisciplinesPermissionsService.canManageAllDisciplinesOwnFederation && this.isCurrentUserPartOfFederation;
      return canManageCurrentDisciplineAndIsPartOfCurrentDiscipline || canManageAllDisciplines;
    },
  },

  async mounted() {
    await this.initPage();
  },

  methods: {
    async initPage() {
      // Override the props values from the url if provided
      this.federationId = this.$route.params.federationId ? parseInt(this.$route.params.federationId) : null;
      this.disciplineId = this.$route.params.disciplineId ? parseInt(this.$route.params.disciplineId) : this.disciplineId_prop;

      // Check if the current user is part of current federation
      this.isCurrentUserPartOfFederation = await getIsUserPartOfFederationAsync(this.authService.userId, this.federationId);
      // Check if the current user is part of current discipline
      this.isCurrentUserPartOfDiscipline = await getIsManagerPartOfDisciplineAsync(this.authService.userId, this.disciplineId);

      // Check if the user can see this page
      if (!this.canViewCurrentDiscipline) {
        store.dispatch("setSnackbar", {
          title: `Non Autorisé`,
          text: `Vous n'êtes pas autorisé à consulter cette ressource.`,
          status: "warning",
          timeout: 2000,
        });

        this.$router.replace("/");
      }

      this.getDisciplineForCustomLabel();
      this.getAgeGroupsForDiscipline();
    },

    // get the custom title if exists
    getCutomTitle() {
      if (this.currentDiscipline && this.currentDiscipline.ageGroupCustomTitle) {
        return this.currentDiscipline.ageGroupCustomTitle;
      } else {
        return "Catégorie d’âge";
      }
    },

    // Add a new Age Group to the discipline
    async addAgeGroup() {
      this.newAgeGroup.disciplineId = this.disciplineId;

      var result = await createAgeGroupForDisciplineAsync(this.disciplineId, this.newAgeGroup);

      if (result) {
        store.dispatch("setSnackbar", {
          title: "Ajouté",
          text: "L'élément de la liste a bien été ajouté",
          status: "information",
          timeout: 1000,
        });
      }

      this.getAgeGroupsForDiscipline();
    },

    // Delete an Age Group
    async deleteAgeGroup(id) {
      if (!id || !confirm("Êtes-vous sûr de vouloir supprimer cet élément ?")) return false;

      var result = await deleteAgeGroupAsync(id);

      if (result) {
        store.dispatch("setSnackbar", {
          title: "Supprimé",
          text: "L'élément de la liste a bien été supprimé",
          status: "information",
          timeout: 1000,
        });
      }

      this.getAgeGroupsForDiscipline();
    },

    // Update an Age Group
    async updateAgeGroup(ageGroup) {
      if (ageGroup.title) {
        var result = await updateAgeGroupAsync(ageGroup);

        if (result) {
          store.dispatch("setSnackbar", {
            title: "Modifié",
            text: "L'élément de la liste a bien été modifié",
            status: "information",
            timeout: 1000,
          });
        }
        this.getAgeGroupsForDiscipline();
      }
    },

    // Get the list of Age Group
    async getAgeGroupsForDiscipline() {
      this.ageGroups = await getAgeGroupForDisciplineAsync(this.disciplineId);
    },

    // Get the current discipline to use the custom Age Group title
    async getDisciplineForCustomLabel() {
      this.currentDiscipline = await getDisciplineAsync(this.disciplineId);

      if (!this.currentDiscipline) {
        store.dispatch("setSnackbar", {
          title: "Erreur",
          text: "Impossible de récupérer la discipline auprès du serveur",
          status: "error",
          timeout: 5000,
        });
      } else {
        this.customTitle = this.currentDiscipline.ageGroupCustomTitle ? this.currentDiscipline.ageGroupCustomTitle : this.customTitle;
      }
    },

    // Get the current discipline to use the custom Age Group title
    async updateDisciplineCustomLabel(customTitle) {
      if (this.currentDiscipline) {
        this.currentDiscipline.ageGroupCustomTitle = customTitle;

        var result = await updateDisciplineAsync(this.currentDiscipline.id, this.currentDiscipline);

        if (result) {
          store.dispatch("setSnackbar", {
            title: "Succès",
            text: "Le titre personnalisé a bien été mis à jour.",
            status: "success",
            timeout: 1000,
          });
        }

        this.getDisciplineForCustomLabel();
      } else {
        store.dispatch("setSnackbar", {
          title: "Erreur",
          text: "Impossible de récupérer la discipline auprès du serveur",
          status: "error",
          timeout: 5000,
        });
      }
    },
  },
};
</script>

<style>
.part-title {
  font-family: Roboto, sans-serif;
  font-size: 16px;
  font-weight: 500;
}
.no-vert-padding {
  padding-top: 0;
  padding-bottom: 0;
}
</style>
